/**
 * API client. Talks to /api/v1 with credentials (the shared credify_token cookie
 * is sent automatically). In local dev, an impersonated user is passed via the
 * `x-dev-user` header — the same seam the API's dev-signin path reads.
 */
import { RULES_CHANGED, TASKS_CHANGED } from './liveEvents.js';

const BASE = '/api/v1';

let devUser: string | null = null;
export function setDevUser(email: string | null): void {
  devUser = email;
}
export function getDevUser(): string | null {
  return devUser;
}

// Admin "view as" (prod). When set, the API honors it only if the real SSO user
// is an admin (see requireUser). Sent on every request alongside the SSO cookie.
let impersonateEmail: string | null = null;
export function setImpersonate(email: string | null): void {
  impersonateEmail = email;
}

/**
 * ehr#279 - the shared SSO cookie lasts a day (30 with "remember me") and nothing renews it, so a
 * tab left open outlives it: every call then answers 401 and a save showed a generic "Could not
 * …". A 401 on any call but the startup /auth/me check (which has its own sign-in screen) tells
 * the app, which asks the person to sign in again without leaving the page.
 */
export const SESSION_EXPIRED_MESSAGE = 'Your sign-in has expired. Sign in again to continue.';
type ExpiredListener = () => void;
const expiredListeners = new Set<ExpiredListener>();
export function onSessionExpired(fn: ExpiredListener): () => void {
  expiredListeners.add(fn);
  return () => {
    expiredListeners.delete(fn);
  };
}

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    /** ehr#405: the server's details (e.g. which fields failed validation), when it sent any. */
    public details?: unknown,
  ) {
    super(message);
  }
}

/** ehr#405: what the person sees when the server couldn't be reached at all. */
export const NETWORK_ERROR_MESSAGE =
  'Could not reach the server. Check your connection and try again.';

// #107 - after a successful task mutation, broadcast so surfaces that summarize
// tasks (e.g. the sidebar overdue badge) can refresh live instead of only on
// navigation/refresh. Fires for any non-GET to a /tasks path.
function notifyTaskMutation(method: string, path: string): void {
  if (method === 'GET') return;
  if (typeof window === 'undefined') return;
  if (path === '/tasks' || path.startsWith('/tasks/') || path.startsWith('/tasks?')) {
    window.dispatchEvent(new CustomEvent(TASKS_CHANGED));
  }
}

// ehr#177/#178 - after a successful contact mutation (the drawer's owner, archive, name,
// status and Demographics saves, a new contact, a CSV import), tell the Contacts list to
// refetch, so the change shows without a page refresh.
export const CONTACTS_CHANGED = 'credify:contacts-changed';
function notifyContactMutation(method: string, path: string): void {
  if (method === 'GET') return;
  if (typeof window === 'undefined') return;
  if (
    path === '/contacts' ||
    path.startsWith('/contacts/') ||
    path.startsWith('/contacts?') ||
    path.startsWith('/import/contacts')
  ) {
    window.dispatchEvent(new CustomEvent(CONTACTS_CHANGED));
  }
}

// credifyfbs#313 - after a successful form-rule write (save, edit, pause, activate, duplicate,
// delete), tell the Forms Hub so its Form Rules badge recounts at once.
function notifyRuleMutation(method: string, path: string): void {
  if (method === 'GET') return;
  if (typeof window === 'undefined') return;
  if (path === '/rules' || path.startsWith('/rules/') || path.startsWith('/rules?')) {
    window.dispatchEvent(new CustomEvent(RULES_CHANGED));
  }
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  if (devUser) headers['x-dev-user'] = devUser;
  if (impersonateEmail) headers['x-impersonate-email'] = impersonateEmail;

  let res: Response;
  try {
    res = await fetch(BASE + path, {
      method,
      headers,
      credentials: 'include',
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  } catch {
    // ehr#405: a dropped connection said "Failed to fetch" (or nothing); say what happened instead
    throw new ApiError(0, 'NETWORK', NETWORK_ERROR_MESSAGE);
  }

  if (res.status === 204) {
    notifyTaskMutation(method, path);
    notifyContactMutation(method, path);
    notifyRuleMutation(method, path);
    return undefined as T;
  }

  const json = (await res.json().catch(() => null)) as
    { data: T } | { error: { code: string; message: string; details?: unknown } } | null;

  if (!res.ok || !json || 'error' in json) {
    const err: { code: string; message: string; details?: unknown } =
      json && 'error' in json ? json.error : { code: 'UNKNOWN', message: res.statusText };
    // ehr#405: never an empty message (a proxy's 502 page has no JSON, and HTTP/2 has no status text)
    if (!err.message) err.message = `The request failed (HTTP ${res.status}). Please try again.`;
    if (res.status === 401 && path !== '/auth/me') {
      for (const fn of expiredListeners) fn();
      throw new ApiError(401, err.code, SESSION_EXPIRED_MESSAGE);
    }
    throw new ApiError(res.status, err.code, err.message, err.details);
  }
  notifyTaskMutation(method, path);
  notifyContactMutation(method, path);
  notifyRuleMutation(method, path);
  return json.data;
}

/** The server's own message for an API error (e.g. "Only admins or managers…"), else the fallback. */
export function apiErrorText(e: unknown, fallback: string): string {
  return e instanceof ApiError && e.message ? e.message : fallback;
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body),
  put: <T>(path: string, body?: unknown) => request<T>('PUT', path, body),
  patch: <T>(path: string, body?: unknown) => request<T>('PATCH', path, body),
  del: <T>(path: string) => request<T>('DELETE', path),
};
